import { useEffect, useMemo } from "react";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import { cn } from "utils";
import { uniq } from "lodash-es";
import { useSnapshot } from "valtio";
import { EmptyState } from "./EmptyState";
import { HotItem } from "./HotItem";
import { LoadingState } from "./LoadingState";
import {
  Content,
  ContentCategoryEnum,
  OpenDataTypeEnum,
  ThemeItem,
  TokenSortField,
} from "@features/api/openData";
import { SCOPE_MAP } from "@const/recommendQuestion";
import { ThemesScopeEnum } from "@const/market";
import {
  TokensQuestParams,
  useTokensRanking,
} from "@/src/hooks/useTokensRanking";
import { useAIInsightDetail } from "@hooks/useAIInsightDetail";
import { useGoToDetail } from "@hooks/useGoToDetail";
import { useIsPc } from "@hooks/useIsPc";
import { useRecommendQuestion } from "@hooks/useRecommendQuestion";
import { useThemesInfiniteQuery } from "@hooks/useThemesInfiniteQuery";

import { CRYPTO_THEME_LABELS } from "@componentsv1/radar/consts/themes";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Menu/MenuSearchModal/RecommendArea/HotCrypto";
import { favoritesCenter } from "@store/favorite";
import { rootStore } from "@store/rootStore";
import { useHotNews } from "@/src/hooks/useHotNews";
import { useTokensByIds } from "@/src/hooks/useTokensByIds";

const I18n = I18nUtil.createScoped(Translations);

export const HotCrypto = ({
  onClose,
  type,
}: {
  onClose: () => void;
  type?: "normal" | "portfolio";
}) => {
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const locale = I18nUtil.getCurrentLocale();
  const { onDetail, isLoading } = useGoToDetail("crypto");

  const [isPc] = useIsPc();

  const {
    data: { data: hotCryptos },
    isLoading: cryptoRequestAgentLoading,
  } = useTokensRanking({
    queryParams: {
      limit: 4,
      [TokensQuestParams.Enrich_With_Rating]: false,
      [TokensQuestParams.Sort_Field]: TokenSortField.TURNOVER_RATE,
      [TokensQuestParams.Sort_Order]: "DESC",
    },
    homeCategory: CRYPTO_THEME_LABELS.blueChip,
    enabled: type === "normal",
  });

  // 从 hotCryptos 中提取每个币的第一个 theme_id，去重后用于查询主题详情
  const cryptoThemeIds = useMemo(() => {
    if (type !== "normal" || !Array.isArray(hotCryptos)) {
      return [] as string[];
    }
    const ids =
      hotCryptos
        ?.map((item: any) => item?.theme_ids?.[0])
        ?.filter((id: unknown): id is string => typeof id === "string") ?? [];
    return uniq(ids);
  }, [hotCryptos, type]);

  const { data } = useThemesInfiniteQuery({
    enabled: false,
    type: OpenDataTypeEnum.TOKEN,
    sortOrder: "ASC",
    limit: isPc ? 8 : 4,
    scope: ThemesScopeEnum.HotSearch,
  });

  const hotThemes = data?.pages.flatMap((page) => page.rows);

  const queryParams = {
    [`scopes[${SCOPE_MAP.TOKEN_PORTFOLIO_SEARCH}]`]: 6,
  };
  const { data: recommendData, isLoading: recommendLoading } =
    useRecommendQuestion(queryParams, {
      enabled: type === "portfolio",
      staleTime: 1000 * 60 * 5,
    });
  const ids = useMemo(() => {
    if (!recommendData) {
      return [];
    }
    return uniq(
      Object.values(recommendData)
        .flat()
        .map((item: any) => item.id)
        .filter((id): id is string => !!id),
    );
  }, [recommendData]);
  const { data: tokensByIds, isLoading: tokensByIdsLoading } = useTokensByIds({
    ids,
    type: "crypto",
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
  const portfolio = useMemo(() => {
    if (!recommendData || !Object.keys(recommendData)?.length) {
      return [];
    }
    if (!tokensByIds || !Object.keys(tokensByIds)?.length) {
      return [];
    }
    if (ids.length !== Object.keys(tokensByIds)?.length) {
      return [];
    }
    const portfolio = ids.map((id) => {
      if (!tokensByIds[id]) {
        return [];
      }
      return {
        ...tokensByIds[id],
        trade: {
          price_usdt: tokensByIds[id]?.price,
          price_change_24h: tokensByIds[id]?.change,
        },
        id: id,
      };
    });
    return portfolio;
  }, [tokensByIds, recommendData, ids]);
  const HotCrypto = useMemo(() => {
    if (type === "portfolio") {
      return portfolio;
    }
    return hotCryptos;
  }, [portfolio, hotCryptos, type]);

  useEffect(() => {
    if (!HotCrypto?.length) {
      return;
    }
    const ids = HotCrypto?.map((item: any) => item?.id);
    if (isLoggedIn && ownerInfo?.id) {
      favoritesCenter.checkBatchFavoritesStatus(ids, "TOKEN");
    }
  }, [HotCrypto, ownerInfo?.id, isLoggedIn]);
  const recommendThemes = hotThemes
    ?.map((item) => item?.id)
    ?.filter((id) => typeof id === "string");
  const { data: hotNews } = useHotNews({
    // enabled: type === "normal",
    enabled: false,
    initialParams: {
      locale: locale,
      limit: 4,
      offset: 0,
      themeIds: recommendThemes,
      category: ContentCategoryEnum.CRYPTO,
      symbols: [],
    },
  });

  // 从 hotNews 中提取每条新闻的第一个 theme_id，去重
  const newsThemeIds = useMemo(() => {
    if (type !== "normal" || !Array.isArray(hotNews)) {
      return [] as string[];
    }
    const ids =
      hotNews
        ?.map((item: Content) => item?.theme_ids?.[0])
        ?.filter((id: unknown): id is string => typeof id === "string") ?? [];
    return uniq(ids);
  }, [hotNews, type]);

  // 合并 crypto 与 news 的 theme_id，统一一次批量查询（与 HotStock 逻辑保持一致）
  const allThemeIds = useMemo(
    () => uniq([...cryptoThemeIds, ...newsThemeIds]),
    [cryptoThemeIds, newsThemeIds],
  );

  const { data: themesDetailList } = useAIInsightDetail({
    themeId: allThemeIds.join(","),
    type: OpenDataTypeEnum.TOKEN,
    enabled: allThemeIds.length > 0 && type === "normal",
  });

  const themeMap = useMemo(() => {
    const map = new Map<string, ThemeItem>();
    (themesDetailList || []).forEach((theme) => {
      if (theme?.id) {
        map.set(theme.id, theme as ThemeItem);
      }
    });
    return map;
  }, [themesDetailList]);
  return (
    <div className="flex flex-col gap-6 md:gap-8">
      {type === "normal" && (
        <div className="flex flex-col gap-3">
          <p className="md:title_s title_m text-content_tertiary">
            {I18n.t("Hot Themes")}
          </p>
          <div className="flex flex-wrap gap-3">
            {hotThemes?.map((item) => (
              <ShellLink
                aria-label="Hot Crypto"
                data-openreplay-label="home_hotcrypto_open"
                id="lnk-hot-crypto"
                key={item?.slug}
                href={`/market/crypto/${item?.slug}`}
                className="px-2 py-[6px] rounded-md bg-background_white_overlay5 body_medium_s text-content_secondary hover:text-content_primary cursor-pointer"
                onClick={() => onClose?.()}
              >
                {item?.theme}
              </ShellLink>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <p className="md:title_s title_m text-content_tertiary">
          {I18n.t("Hot Crypto")}
        </p>
        {(cryptoRequestAgentLoading ||
          recommendLoading ||
          tokensByIdsLoading) && (
          <LoadingState
            count={type === "normal" ? 4 : 6}
            className={cn("grid-cols-1 md:grid-cols-2")}
          />
        )}
        {HotCrypto?.length > 0 && (
          <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
            {HotCrypto?.map((item: any) => {
              const firstThemeId = item?.theme_ids?.[0];
              const matchedTheme = firstThemeId && themeMap.get(firstThemeId);
              const themeValue = matchedTheme || item?.themes?.[0] || undefined;
              return (
                <HotItem
                  {...item}
                  onDetail={(record: any) => {
                    onDetail(record);
                    setTimeout(() => {
                      onClose?.();
                    }, 1000);
                  }}
                  type="crypto"
                  isLoading={isLoading}
                  key={item.id}
                  image={item?.socials?.image_url || item?.logo}
                  price={item?.trade?.price_usdt}
                  change={item?.trade?.price_change_24h}
                  name={item?.name}
                  symbol={item?.symbol}
                  themeValue={themeValue}
                  originType={type}
                />
              );
            })}
          </div>
        )}
        {!cryptoRequestAgentLoading &&
          !recommendLoading &&
          !tokensByIdsLoading &&
          HotCrypto?.length === 0 &&
          type === "normal" && <EmptyState />}
      </div>
    </div>
  );
};
